import {Component, OnInit, viewChild} from '@angular/core';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {ToolLocationService} from '../../../service/tool-location.service';
import {CantaaMessageService} from '../../../service/cantaa-message.service';
import {BookMobileLocationDto, BookToolDto, ToolBookingDto} from '../../../model/booking.models';
import {ToolLocationDetail} from '../../../model/tool-location-detail.model';
import {TransactionService} from '../../../service/transaction.service';
import {CantaaErrorHandlerService} from '../../../service/cantaa-error-handler.service';
import {MatFormField} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {TranslateModule} from '@ngx-translate/core';
import {ToolLocationDocumentService} from "../../../service/tool-location-document.service";
import {SuggestionInputComponent} from '../../inputs/suggestion-input/suggestion-input.component';
import {ToolLocationListItem} from '../../../model/tool-location-list-item.model';
import {DisplayHelper} from '../../../service/helper/DisplayHelper';
import {ProjectListItem} from "../../../model/project-list.item";
import {ProjectService} from '../../../service/project.service';
import {ProjectDetailItem} from "../../../model/project-detail.item";
import {MatButton} from "@angular/material/button";

@Component({
  selector: 'wim-location-booking',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    TranslateModule,
    SuggestionInputComponent,
    MatButton
  ],
  templateUrl: './mobile-location-booking.component.html',
  styleUrl: './mobile-location-booking.component.scss'
})
export class MobileLocationBookingComponent implements OnInit {
  protected readonly DisplayHelper = DisplayHelper;
  employeeSuggestionInput = viewChild.required<SuggestionInputComponent<ToolLocationListItem>>('employeeSuggestionInput');
  mobileLocationSuggestionInput = viewChild.required<SuggestionInputComponent<ToolLocationListItem>>('mobileLocationSuggestionInput');
  toProjectSuggestionInput = viewChild.required<SuggestionInputComponent<ProjectListItem>>('toProjectSuggestionInput');

  selectedEmployee: ToolLocationDetail | null = null;
  selectedMobileLocation: ToolLocationDetail | null = null;
  selectedToProject: ProjectDetailItem | null = null;

  statusText = '';

  constructor(private toolLocationService: ToolLocationService,
              private transactionService: TransactionService,
              private messageService: CantaaMessageService,
              private toolLocationDocumentService: ToolLocationDocumentService,
              private errorHandler: CantaaErrorHandlerService,
              private projectService: ProjectService) {
  }

  ngOnInit(): void {

    this.resetBookingForm();
  }

  async onEmployeeSelected(toolLocationListItem: ToolLocationListItem | null) {

    if (toolLocationListItem == null) {
      return
    }

    try {
      this.selectedEmployee = await this.toolLocationService.getToolLocationDetailById(toolLocationListItem.id, false);
      this.mobileLocationSuggestionInput().enable();
      this.mobileLocationSuggestionInput().focus();
      this.messageService.success('EMPLOYEE.FOUND');
      this.toolLocationDocumentService.getDefaultPhotoScaled70x70_2ImgEleId(this.selectedEmployee.id, 'mobilelocationbooking_employee_icon');
    } catch (e) {
      const error = this.errorHandler.handleError(e, 'FAILED_TO_FETCH');
      this.addMessageToStatus('error: ' + error);
      this.selectedEmployee = null;
    }
  }

  async onMobileLocationSelected(toolLocationListItem: ToolLocationListItem | null) {

    if (!toolLocationListItem) {
      return;
    }

    try {
      this.selectedMobileLocation = await this.toolLocationService.getToolLocationDetailById(toolLocationListItem.id, false);

      this.messageService.success('MOBILE_LOCATION.FOUND');

      this.toolLocationDocumentService.getDefaultPhotoScaled70x70_2ImgEleId(this.selectedMobileLocation.id, 'mobilelocationbooking_mobilelocation_icon');

      if (this.selectedMobileLocation.currentProjectId && (this.selectedEmployee?.locationTypeCode != "STOCK")) {
        await this.selectProject(this.selectedMobileLocation.currentProjectId);
      }

      this.toProjectSuggestionInput().enable();
      this.toProjectSuggestionInput().focus();
    } catch (e) {
      const error = this.errorHandler.handleError(e, 'FAILED_TO_FETCH');
      this.addMessageToStatus('error: ' + error);
      this.selectedMobileLocation = null;
      throw e;
    }
  }

  private async selectProject(projectId: number) {
    this.selectedToProject = await this.projectService.getProjectDetail(projectId);

    this.toProjectSuggestionInput().setValue(this.toProjectListItem(this.selectedToProject));
    this.messageService.success('PROJECT.FOUND');

    this.employeeSuggestionInput().disable();
    this.mobileLocationSuggestionInput().disable();
  }

  private toProjectListItem(project: ProjectDetailItem): ProjectListItem {
    return {
      id: project.id!,
      treeNodeLabel: '',
      treeNode: null as any,
      rfid: '',
      projectType: project.projectType,
      number: project.number,
      status: project.status,
      startDate: project.startDate ?? '',
      terminationDate: project.terminationDate ?? '',
      amount: 0
    };
  }

  async onToProjectSelected(project: ProjectListItem | null) {

    if (project) {
      await this.selectProject(project.id);
    } else {
      this.selectedToProject = null;
    }
  }

  async bookMobileLocation() {
    const bookLocationDto = {
      transactionTypeCode: "HandoverLocation",
      toLocationId: this.selectedEmployee?.id,
      mobileLocationId: this.selectedMobileLocation?.id,
      toProjectId: this.selectedToProject?.id
    } as BookMobileLocationDto;

    await this.book(bookLocationDto);

    this.messageService.success('BOOKING.SUCCESSFUL');

    const statusText = this.createStatusText();
    this.addMessageToStatus(statusText);
  }

  private async book(bookLocationDto: BookMobileLocationDto) {
    try {
      return await this.transactionService.makeBooking(bookLocationDto, false)
    } catch (e) {
      const error = this.errorHandler.handleError(e, 'BOOKING.FAILED');
      this.addMessageToStatus('error: ' + error);
      throw e;
    }
  }

  createStatusText(): string {
    return this.selectedMobileLocation?.locationPretty + ' -> ' + this.selectedEmployee?.locationPretty;
  }

  addMessageToStatus(msg: string) {
    this.statusText = msg + '\n' + this.statusText;
  }

  employeeFetchSuggestions = async (query: string): Promise<ToolLocationListItem[]> => {
    return await this.toolLocationService.searchLocations(query, false);
  };

  mobileLocationFetchSuggestions = async (query: string): Promise<ToolLocationListItem[]> => {
    return await this.toolLocationService.searchLocations(query, true);
  };

  toProjectFetchSuggestions = async (query: string): Promise<ProjectListItem[]> => {

    const projects = await this.projectService.searchProjectsByQuery(query);
    return projects;
  };

  async onBooking() {

    await this.bookMobileLocation();

    this.resetBookingForm();
  }

  async onCancel() {

    this.resetBookingForm();
  }

  private resetBookingForm(): void {

    this.selectedEmployee = null;
    this.selectedMobileLocation = null;
    this.selectedToProject = null;

    this.employeeSuggestionInput().clear();
    this.toProjectSuggestionInput().clear();
    this.mobileLocationSuggestionInput().clear();

    this.employeeSuggestionInput().enable();
    this.employeeSuggestionInput().focus();
    this.toProjectSuggestionInput().disable();
    this.mobileLocationSuggestionInput().disable();
  }

  isBookingPossible(): boolean {
    return this.selectedEmployee !== null
      && this.selectedMobileLocation !== null;
  }
}
